import { Group, Loader, Modal, MultiSelect, Skeleton, Stack, Text, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { usePortfolios } from '../../../../api/portfolio-client/portfolio-client.hooks';
import type { ProjectsResponse } from '../../../../api/projects-client/projects-client.type';
import { Button } from '../../../../components/button/button';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Select } from '../../../../ui-library/select/select';
import { useCreateProject } from './project.hooks';
import type { CreateProjectFormValues } from './projects.type';

function CreateProjectWizard({
  projects,
  opened,
  onClose,
  onSubmit,
}: {
  projects: ProjectsResponse[];
  opened: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const currentOrganization = useGlobalStore((state) => state.organization);
  const { portfolios, query: portfoliosQuery } = usePortfolios({
    queryKey: ['portfolios'] as const,
  });
  const availablePortfolios = portfolios.filter((portfolio) => portfolio.organization.id === currentOrganization?.id);
  const selectedPortfolio = useGlobalStore((state) => state.portfolio);

  const { createProject, isCreating } = useCreateProject({ onSuccess: onSubmit });

  const initialValues = {
    name: 'New Project',
    portfolioId: selectedPortfolio?.id || null,
    subprojectIds: [],
  };

  const form = useForm<CreateProjectFormValues>({
    validateInputOnChange: true,
    initialValues,
    validate: {
      name: isNotEmpty(),
      portfolioId: isNotEmpty(),
      subprojectIds: isNotEmpty(),
    },
  });

  const subprojectOptions = projects
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((project) => ({
      group: project.name,
      items: project.subprojects
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((subproject) => ({ value: subproject.id, label: subproject.name })),
    }));

  function handleClose(onClose: () => void) {
    onClose();
    form.reset();
  }

  function handleSubmit() {
    if (!form.values.portfolioId || !form.values.name || !form.values.subprojectIds.length) {
      return;
    }
    createProject({
      name: form.values.name,
      portfolioId: form.values.portfolioId,
      subprojectIds: form.values.subprojectIds,
    });
  }

  return (
    <Modal title="Create A Project" opened={opened} onClose={() => handleClose(onClose)}>
      <Stack gap="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput label="Project Name" {...form.getInputProps('name')} radius="xl" />
            {portfoliosQuery.isFetching ? (
              <Skeleton height={32} radius="md" width="70%" />
            ) : (
              <Stack gap={2}>
                <Text size="sm">Portfolio</Text>
                <Select
                  placeholder="Select a Portfolio"
                  value={form.values.portfolioId}
                  onChange={(value) => form.setFieldValue('portfolioId', value)}
                  options={availablePortfolios.map((portfolio) => ({ label: portfolio.name, value: portfolio.id }))}
                  searchable
                />
              </Stack>
            )}
            <MultiSelect
              label="Boards"
              disabled={subprojectOptions.length === 0}
              placeholder="Select Boards"
              {...form.getInputProps('subprojectIds')}
              data={subprojectOptions}
              searchable
              clearable
              comboboxProps={{ withinPortal: true, zIndex: 1000 }}
              radius="xl"
            />
            <Group justify="center" gap="md">
              <Button variant="outline" radius="xl" onClick={() => handleClose(onClose)}>
                Cancel
              </Button>
              <Button disabled={!form.isValid() || isCreating} type="submit" radius="xl">
                {isCreating ? <Loader size="sm" /> : 'Create'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Modal>
  );
}

export { CreateProjectWizard };
